import * as React from 'react'
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material'
import useUserSettingsStore from '../../store/userSettings'

function padZero(n: number) {
  return n < 10 ? `0${n}` : String(n)
}

function to12Hour(h24: number) {
  const ampm = h24 >= 12 ? 'PM' : 'AM'
  let h = h24 % 12
  if (h === 0) h = 12
  return { h, ampm }
}

const HOUR_WORDS: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
}

const MINUTE_WORDS: Record<number, string> = {
  0: 'zero',
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
  13: 'thirteen',
  14: 'fourteen',
  15: 'fifteen',
  16: 'sixteen',
  17: 'seventeen',
  18: 'eighteen',
  19: 'nineteen',
  20: 'twenty',
  21: 'twenty one',
  22: 'twenty two',
  23: 'twenty three',
  24: 'twenty four',
  25: 'twenty five',
  26: 'twenty six',
  27: 'twenty seven',
  28: 'twenty eight',
  29: 'twenty nine',
  30: 'thirty',
}

function capitalize(input: string) {
  if (!input) {
    return input
  }
  return input.charAt(0).toUpperCase() + input.slice(1)
}

function lowercaseFirst(input: string) {
  if (!input) {
    return input
  }
  return input.charAt(0).toLowerCase() + input.slice(1)
}

const ITS = 'It’s'

function getHourWord(hour: number) {
  const normalized = ((hour - 1) % 12) + 1
  return HOUR_WORDS[normalized]
}

export function getTimeInWords(hours12: number, minutes: number, format: 'minutes' | 'plain' = 'minutes') {
  if (!Number.isFinite(hours12) || !Number.isFinite(minutes)) {
    return ''
  }

  const hourWord = getHourWord(hours12)
  if (!hourWord) {
    return ''
  }

  if (minutes === 0) {
    const base = `${hourWord}${format === 'minutes' ? " o'clock" : ''}`
    return capitalize(base)
  }

  if (minutes === 30) {
    return capitalize(`half past ${hourWord}`)
  }

  if (minutes < 30) {
    const minuteWord = MINUTE_WORDS[minutes]
    if (!minuteWord) {
      return ''
    }
    const minuteLabel = minutes === 1 ? 'minute' : 'minutes'
    const phrase = format === 'minutes'
      ? `${minuteWord} ${minuteLabel} past ${hourWord}`
      : `${minuteWord} past ${hourWord}`
    return capitalize(phrase)
  }

  const minutesTo = 60 - minutes
  const nextHour = hours12 === 12 ? 1 : hours12 + 1
  const nextHourWord = getHourWord(nextHour)
  const minuteWord = MINUTE_WORDS[minutesTo]
  if (!minuteWord || !nextHourWord) {
    return ''
  }

  const minuteLabel = minutesTo === 1 ? 'minute' : 'minutes'
  const phrase = format === 'minutes'
    ? `${minuteWord} ${minuteLabel} to ${nextHourWord}`
    : `${minuteWord} to ${nextHourWord}`

  return capitalize(phrase)
}

export function formatLiteralTime(date: Date): string {
  const hours24 = date.getHours()
  const minutes = date.getMinutes()
  const { h: hours12 } = to12Hour(hours24)
  const literal = getTimeInWords(hours12, minutes, 'minutes')

  if (!literal) {
    return ''
  }

  return `${ITS} ${lowercaseFirst(literal)}`
}

const ANCHORS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]

const EXACT_WINDOW = 30 // seconds
const JUST_GONE_WINDOW = 210 // seconds (3m30s)
const ALMOST_WINDOW = 210 // seconds (3m30s)

function anchorLabel(minutesMark: number): string {
  switch (minutesMark) {
    case 0:
    case 60:
      return ''
    case 5:
      return 'five'
    case 10:
      return 'ten'
    case 15:
      return 'a quarter'
    case 20:
      return 'twenty'
    case 25:
      return 'twenty five'
    case 30:
      return 'half'
    case 35:
      return 'twenty five'
    case 40:
      return 'twenty'
    case 45:
      return 'a quarter'
    case 50:
      return 'ten'
    case 55:
      return 'five'
    default:
      return ''
  }
}

function isToPhrase(anchor: number) {
  return anchor >= 35 && anchor <= 60
}

export function formatFuzzyTime(date: Date): string {
  const hours24 = date.getHours()
  const minutes = date.getMinutes()
  const seconds = date.getSeconds()
  const { h: hours12 } = to12Hour(hours24)
  const hourWord = getHourWord(hours12)

  if (!hourWord) {
    return ''
  }

  const nextHourWord = getHourWord(hours12 === 12 ? 1 : hours12 + 1)

  if (!nextHourWord) {
    return ''
  }

  const totalSeconds = minutes * 60 + seconds

  let previousAnchor = 0
  for (const anchor of ANCHORS) {
    if (anchor * 60 <= totalSeconds) {
      previousAnchor = anchor
    }
  }

  const nextAnchor = ANCHORS.find((anchor) => anchor * 60 > totalSeconds) ?? 60

  const previousAnchorSeconds = previousAnchor * 60
  const nextAnchorSeconds = nextAnchor * 60

  const deltaFromPrevious = totalSeconds - previousAnchorSeconds
  const deltaToNext = nextAnchorSeconds - totalSeconds

  const formatExactPhrase = (anchor: number) => {
    if (anchor === 0) {
      return `${ITS} ${hourWord} o'clock`
    }

    if (anchor === 60) {
      return `${ITS} ${nextHourWord} o'clock`
    }

    const label = anchorLabel(anchor)
    if (!label) {
      return ''
    }

    if (isToPhrase(anchor)) {
      return `${ITS} ${label} to ${nextHourWord}`
    }

    return `${ITS} ${label} past ${hourWord}`
  }

  if (deltaFromPrevious <= EXACT_WINDOW) {
    const phrase = formatExactPhrase(previousAnchor)
    if (phrase) {
      return phrase
    }
  }

  if (deltaToNext <= EXACT_WINDOW) {
    const phrase = formatExactPhrase(nextAnchor)
    if (phrase) {
      return phrase
    }
  }

  if (previousAnchor > 0 && deltaFromPrevious > EXACT_WINDOW && deltaFromPrevious <= JUST_GONE_WINDOW) {
    const label = anchorLabel(previousAnchor)
    if (!label) {
      return ''
    }

    if (isToPhrase(previousAnchor)) {
      return `${ITS} just gone ${label} to ${nextHourWord}`
    }

    return `${ITS} just gone ${label} past ${hourWord}`
  }

  if (previousAnchor === 0 && deltaFromPrevious > EXACT_WINDOW && deltaFromPrevious <= JUST_GONE_WINDOW) {
    return `${ITS} just gone ${hourWord} o'clock`
  }

  if (nextAnchor > 0 && deltaToNext > EXACT_WINDOW && deltaToNext <= ALMOST_WINDOW) {
    if (nextAnchor === 60) {
      return `${ITS} almost ${nextHourWord} o'clock`
    }

    const label = anchorLabel(nextAnchor)
    if (!label) {
      return ''
    }

    if (isToPhrase(nextAnchor)) {
      return `${ITS} almost ${label} to ${nextHourWord}`
    }

    return `${ITS} almost ${label} past ${hourWord}`
  }

  const midpoint = (previousAnchorSeconds + nextAnchorSeconds) / 2
  const chosenAnchor = totalSeconds < midpoint ? previousAnchor : nextAnchor

  if (chosenAnchor === 0) {
    return `${ITS} ${hourWord} o'clock`
  }

  if (chosenAnchor === 60) {
    return `${ITS} ${nextHourWord} o'clock`
  }

  const label = anchorLabel(chosenAnchor)

  if (!label) {
    return ''
  }

  if (isToPhrase(chosenAnchor)) {
    return `${ITS} ${label} to ${nextHourWord}`
  }

  return `${ITS} ${label} past ${hourWord}`
}

const ClockWidget: React.FC = () => {
  const [now, setNow] = React.useState<Date>(() => new Date())
  const clockFuzzy = useUserSettingsStore((state) => state.clockFuzzy)

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const hours24 = now.getHours()
  const minutes = now.getMinutes()
  const seconds = now.getSeconds()
  const { h: hours12, ampm } = to12Hour(hours24)

  const hh = padZero(hours12)
  const mm = padZero(minutes)
  const ss = padZero(seconds)

  const numeric = `${hh}:${mm}:${ss} ${ampm}`
  const primaryText = clockFuzzy ? formatFuzzyTime(now) : formatLiteralTime(now)
  const displayText = primaryText || numeric
  const ariaLabel = primaryText ? `${primaryText} (${numeric})` : numeric

  const progress = ((59 - seconds) / 59) * 100

  return (
    <Box
      aria-label="Clock"
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 2px',
        alignItems: 'center',
        minWidth: 120,
      }}
    >
      <Tooltip title={numeric}>
        <Typography
          component="time"
          dateTime={now.toISOString()}
          variant="body2"
          sx={{
            lineHeight: 1,
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
          aria-label={ariaLabel}
        >
          {displayText}
        </Typography>
      </Tooltip>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mt: 0.5,
          height: 2,
          '& .MuiLinearProgress-bar': { transition: 'none' },
        }}
        aria-hidden={true}
      />
    </Box>
  )
}

export default React.memo(ClockWidget)

export { padZero, to12Hour }
